import * as rxjs from "rxjs";
import * as rxjsOperators from "rxjs/operators";
import { TActionObservableWithPayload } from "../../state/applyActions";
import { TState } from "./lensBaseTypes";
import { reduceDataToStateUpdate } from "./reduceDataToStateUpdate";
import { set } from "./set";
import {TSetState as TSetState} from "../../state/TSetState";
import {TGetState as TGetState} from "../../state/TGetState";
import { TPayloadActionType } from "../../state/actions/TAction";

interface ISetValue<A extends TPayloadActionType, T, S> {
    // 5 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2],
        K4 extends keyof S[K1][K2][K3],
        K5 extends keyof S[K1][K2][K3][K4]
    >
    (
        path: [K1, K2, K3, K4, K5],
        updateFormCallback: (
            value: S[K1][K2][K3][K4][K5],
            data: T,
        ) => S[K1][K2][K3][K4][K5],
    ): (
        obs$: TActionObservableWithPayload<A, T>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
    // 4 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2],
        K4 extends keyof S[K1][K2][K3]
    >
    (
        path: [K1, K2, K3, K4],
        updateFormCallback: (
            value: S[K1][K2][K3][K4],
            data: T,
        ) => S[K1][K2][K3][K4],
    ): (
        obs$: TActionObservableWithPayload<A, T>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
    // 3 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2]
    >
    (
        path: [K1, K2, K3],
        updateFormCallback: (
            value: S[K1][K2][K3],
            data: T,
        ) => S[K1][K2][K3],
    ): (
        obs$: TActionObservableWithPayload<A, T>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
    // 2 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
    >
    (
        path: [K1, K2],
        updateFormCallback: (
            value: S[K1][K2],
            data: T,
        ) => S[K1][K2],
    ): (
        obs$: TActionObservableWithPayload<A, T>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
    // 1 level lens path guard
    <
        K1 extends keyof S
    >
    (
        path: [K1],
        updateFormCallback: (
            value: S[K1],
            data: T,
        ) => S[K1],
    ): (
        obs$: TActionObservableWithPayload<A, T>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
}

export const setValue =
    <A extends TPayloadActionType, T>(): ISetValue<A, T, TState> =>
        // There is no way to create paramater overloads here so we set to any
        (
            lens: any, // eslint-disable-line
            updateFormCallback: (
                value: any, // eslint-disable-line
                data: T,
            ) => any, // eslint-disable-line
        ) =>
            (
                obs$: TActionObservableWithPayload<A, T>,
                getState: TGetState,
                setState: TSetState
            ) =>
                obs$.pipe(
                    rxjsOperators.tap((action) => {
                        reduceDataToStateUpdate<T>(setState)(
                            set<T>()(
                                lens,
                                (value) => updateFormCallback(value, action.payload as any) as never // eslint-disable-line
                            ),
                        )(action.payload as any); // eslint-disable-line
                    })
                );
